import { auth, myOrganizations, restoreSession } from './auth';
import { readClientInvite } from './clientInvite';
import { signupLinkAtStart } from './signupLink';
import { whoFromToken, type Who } from './session';

/*
 * Someone opening the sign-up page who is signed in already: with an
 * organization they are told so (and can go on, or sign out); without one
 * they go to make it. Never blocks the page: Keycloak gets a few seconds,
 * then the form stays. Never while a signup is under way on this page
 * (an invitation, a Google start, the account step's own state).
 */

export type Visitor =
    | { kind: 'anonymous' }
    | { kind: 'has-org'; who: Who; org: string }
    | { kind: 'needs-org'; who: Who };

export const VISITOR_CHECK_MS = 3000;

const within = <T>(p: Promise<T>, ms: number, fallback: T) =>
    Promise.race([p, new Promise<T>((r) => window.setTimeout(() => r(fallback), ms))]);

/** Whether this visit should ask at all. */
// A session this tab's signup made still counts: back on "/", the person is
// signed in and is offered "Continue as" (on to their org step), not a blank form.
export const shouldCheckVisitor = (hasStartedSignup: boolean) =>
    !hasStartedSignup && !readClientInvite() && !signupLinkAtStart().google;

export async function checkVisitor(ms = VISITOR_CHECK_MS): Promise<Visitor> {
    const started = Date.now();
    const state = await within(restoreSession(ms).catch(() => 'unknown' as const), ms, 'unknown' as const);
    if (state !== 'signed-in') return { kind: 'anonymous' };
    // Their organizations, in what is left of the time; no answer decides nothing.
    const left = Math.max(ms - (Date.now() - started), 500);
    const orgs = await within(myOrganizations().catch(() => null), left, null);
    if (orgs === null) return { kind: 'anonymous' };
    const who = whoFromToken(auth.getAccessToken()) ?? {};
    const first = orgs[0];
    return first ? { kind: 'has-org', who, org: first.displayName || first.organizationName } : { kind: 'needs-org', who };
}
